import React, { useEffect } from 'react';
import { Formik, FormikProps } from 'formik';
import * as yup from 'yup';

import {
  Snippet,
  SnippetConfigurationColumn,
  SnippetDataDictionary,
} from '../../../services/dataModels';
import {
  DataEntryTableSnippet,
  MatrixSnippet,
  TableSnippet,
  TagSnippet,
  MonitoringSnippet,
  InstructionSnippet,
} from '../@snippets';
import { Accordion } from '../../../components';
import { getConfigurationsForAllRows } from '../../../helpers/Getters';
import Link from '../Link';
import { RouteProp, useNavigation, useRoute } from '../../../navigation/native';
import { Route } from '../../../app/Route';
import { RootStackParamList } from '../../../app/Navigation';

export const INDEX_KEY = '__INDEX';

const indexRowDataSnippet = (snippet: Snippet) => {
  return {
    ...snippet,
    data: [
      {
        ...snippet.data[0],
        rowData: snippet.data[0].rowData.map((v, i) => ({
          ...v,
          [INDEX_KEY]: i,
        })),
      },
    ],
  };
};

const getValidationSnippet = (snippet: Snippet) => {
  if (snippet.type === 'Matrix') {
    return indexRowDataSnippet(snippet);
  }

  if (snippet.type === 'snippetGroup') {
    return {
      ...snippet,
      groupSnippets: snippet.groupSnippets.map((g) => getValidationSnippet(g)),
    };
  }

  return snippet;
};

interface State {
  dirty: boolean;
  valid: boolean;
}

interface SnippetFieldProps {
  snippet: Snippet;
  path: string;
  dirty?: boolean;
  valid?: boolean;
  state?: { get: State; set: (v: State) => void };
  rowData: SnippetDataDictionary[];
}

const SnippetField = ({ path, snippet, dirty, valid, state, rowData }: SnippetFieldProps) => {
  const navigation = useNavigation();
  const { params } = useRoute<RouteProp<RootStackParamList, 'EditingRowListScreen'>>();

  useEffect(() => {
    if (typeof dirty === 'undefined') {
      return;
    }

    if (state.get.dirty !== dirty || state.get.valid !== valid) {
      state.set({ dirty, valid });
    }
  }, [dirty, valid, state]);

  const handleLinkPressed = (index: number) => {
    navigation.navigate(Route.editingRowScreen, {
      ...params,
      lastLink: rowData[index][snippet.configuration.rowTitleKey],
      rowIndex: index,
    });
  };

  switch (snippet.type) {
    case 'RollupDataEntryTable':
    case 'DataEntryTable':
      return <DataEntryTableSnippet path={path} snippet={snippet} data={rowData} />;
    case 'RollupMatrix':
    case 'Matrix':
      return (
        <MatrixSnippet
          path={path}
          configuration={snippet.configuration}
          data={rowData}
          isRollup={snippet.type === 'RollupMatrix'}
        />
      );
    case 'Table': {
      const canEditAllFields = snippet.enableEdit === 'False';
      return (
        <TableSnippet
          path={path}
          configuration={snippet.configuration}
          data={rowData}
          canEditAllFields={canEditAllFields}
        />
      );
    }
    case 'Tag':
      return <TagSnippet path={path} configuration={snippet.configuration} data={rowData} />;
    case 'StandingOrder':
    case 'Task':
      return (
        <>
          {rowData.map((r, i) => (
            <Link
              key={i}
              name={r[snippet.configuration.rowTitleKey] as string}
              handlePress={() => handleLinkPressed(i)}
            />
          ))}
        </>
      );
    case 'Monitoring':
      return <MonitoringSnippet path={path} configuration={snippet.configuration} data={rowData} />;
    case 'Instruction':
      return (
        <InstructionSnippet path={path} configuration={snippet.configuration} data={rowData} />
      );
    default:
      return <Accordion title={snippet.displayName} size={0} disabled={true} />;
  }
};

interface SnippetFormProps {
  snippet: Snippet;
  state: { get: State; set: (v: State) => void };
  selectRowData: (s: Snippet) => [string, SnippetDataDictionary[]];
}

const SnippetForm = React.forwardRef<FormikProps<Snippet>, SnippetFormProps>(
  ({ snippet, selectRowData, state }: SnippetFormProps, ref) => {
    const configurationToValidation = (configuration: SnippetConfigurationColumn) => {
      if (configuration?.controlType === 'Numeric') {
        return {
          [configuration.name]: yup.number().nullable().typeError('Invalid value'),
        };
      }
      return {};
    };

    const createValidation = (snippet: Snippet) => {
      if (!snippet.configuration) {
        if (snippet.groupSnippets) {
          const g = snippet.groupSnippets.map((s) => createValidation(s));

          return yup.object({
            groupSnippets: yup
              .array()
              .of(
                yup.lazy(
                  (v) => g[snippet.groupSnippets.findIndex((a) => a.name === v.name)],
                ) as any,
              ),
          });
        }
        return yup.object();
      }

      const rowConfiguration = getConfigurationsForAllRows(snippet);

      if (snippet.type === 'Matrix') {
        const colCount = snippet.configuration.columnCount;

        return yup.object({
          data: yup.array().of(
            yup.object({
              rowData: yup.array().of(
                yup.lazy((v) =>
                  yup.object(
                    Array.from(Array(colCount).keys()).reduce(
                      (acc, colIndex) => ({
                        ...acc,
                        ...configurationToValidation(
                          rowConfiguration[v[INDEX_KEY] * colCount + colIndex],
                        ),
                      }),
                      {},
                    ),
                  ),
                ) as any,
              ),
            }),
          ),
        });
      }

      return yup.object({
        data: yup.array().of(
          yup.object({
            rowData: yup.array().of(
              yup.object(
                rowConfiguration.reduce(
                  (previous, current) => ({
                    ...previous,
                    ...configurationToValidation(current),
                  }),
                  {},
                ),
              ),
            ),
          }),
        ),
      });
    };

    const validationSnippet = getValidationSnippet(snippet);
    const validationSchema = createValidation(validationSnippet);

    return (
      <Formik
        initialValues={validationSnippet as Snippet}
        enableReinitialize
        onSubmit={() => {}}
        innerRef={ref}
        validationSchema={validationSchema}>
        {({ values, dirty, isValid }) => {
          const [path, rowData] = selectRowData(values);
          return (
            <SnippetField
              path={path}
              snippet={values}
              rowData={rowData}
              dirty={dirty}
              valid={isValid}
              state={state}
            />
          );
        }}
      </Formik>
    );
  },
);

export default SnippetForm;
